import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Sparkles } from 'lucide-react';
import { trackCtaEvent } from '../api';

const HIDE_ON_PATHS = ['/contact', '/admin'];
const DISMISS_KEY = 'hire_cta_dismissed_until';
const VARIANT_KEY = 'hire_cta_variant';
const VIEW_TRACKED_KEY = 'hire_cta_view_tracked';
const SHOW_AFTER_PX = 480;

const VARIANTS = {
  hire_me: { label: 'Hire Me', preview: 'Have a project?' },
  start_project: { label: 'Start a Project', preview: 'Got an idea?' },
  get_quote: { label: 'Get a Quote', preview: 'Need a quote?' },
};

const pickVariant = () => {
  const stored = localStorage.getItem(VARIANT_KEY);
  if (stored && VARIANTS[stored]) return stored;
  const keys = Object.keys(VARIANTS);
  const choice = keys[Math.floor(Math.random() * keys.length)];
  localStorage.setItem(VARIANT_KEY, choice);
  return choice;
};

const StickyHireCTA = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const variant = useMemo(pickVariant, []);
  const copy = VARIANTS[variant];

  const onPath = HIDE_ON_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until && Date.now() < until) {
      setDismissed(true);
      return;
    }
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Track a single "view" event the first time the CTA actually appears
  useEffect(() => {
    if (dismissed || onPath || !visible) return;
    if (sessionStorage.getItem(VIEW_TRACKED_KEY)) return;
    sessionStorage.setItem(VIEW_TRACKED_KEY, '1');
    trackCtaEvent(variant, 'view');
  }, [visible, dismissed, onPath, variant]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 1000 * 60 * 60 * 24));
    setDismissed(true);
  };

  const handleClick = () => {
    trackCtaEvent(variant, 'click');
  };

  if (dismissed || onPath || !visible) return null;

  return (
    <div
      data-testid="sticky-hire-cta"
      data-variant={variant}
      className="fixed bottom-6 left-6 z-40 animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="relative group">
        <span className="absolute -inset-1 rounded-full bg-blue-500/40 blur-md animate-pulse pointer-events-none" aria-hidden="true"></span>
        <div className="relative flex items-center gap-2 bg-blue-600 hover:bg-blue-700 transition-colors text-white rounded-full pl-5 pr-2 py-2 shadow-2xl shadow-blue-600/30 border border-blue-400/30">
          <Sparkles size={16} className="text-blue-100" />
          <Link
            to="/contact"
            onClick={handleClick}
            data-testid="sticky-hire-link"
            className="text-sm font-semibold whitespace-nowrap"
          >
            <span className="hidden sm:inline">{copy.preview} </span>{copy.label}
          </Link>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            data-testid="sticky-hire-dismiss"
            className="ml-2 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickyHireCTA;
